import { useEffect, useRef, useState } from "react";
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  Typography,
} from "@mui/material";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import WifiOffRoundedIcon from "@mui/icons-material/WifiOffRounded";

const NetworkStatusGuard = () => {
  const initiallyOffline = !navigator.onLine;
  const [networkState, setNetworkState] = useState(
    initiallyOffline ? "offline" : "hidden"
  );
  const wasOffline = useRef(initiallyOffline);
  const closeTimer = useRef(null);

  useEffect(() => {
    const handleOnline = () => {
      if (!wasOffline.current) {
        return;
      }

      wasOffline.current = false;
      setNetworkState("restored");
      closeTimer.current = window.setTimeout(() => {
        setNetworkState("hidden");
      }, 1500);
    };

    const handleOffline = () => {
      wasOffline.current = true;
      window.clearTimeout(closeTimer.current);
      setNetworkState("offline");
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.clearTimeout(closeTimer.current);
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  const isRestored = networkState === "restored";

  return (
    <Dialog
      open={networkState !== "hidden"}
      disableEscapeKeyDown
      aria-labelledby="network-dialog-title"
      aria-describedby="network-dialog-description"
      sx={{
        zIndex: (theme) => theme.zIndex.modal + 10000,
        "& .MuiDialog-paper": {
          borderRadius: 3,
          maxWidth: 420,
          width: "calc(100% - 32px)",
        },
      }}
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: "rgba(18, 18, 18, 0.78)",
            backdropFilter: "blur(3px)",
          },
        },
      }}
    >
      <DialogContent sx={{ px: 4, py: 5, textAlign: "center" }}>
        <Box
          sx={{
            alignItems: "center",
            bgcolor: isRestored ? "rgba(46, 125, 50, 0.12)" : "error.lighter",
            borderRadius: "50%",
            color: isRestored ? "success.main" : "error.main",
            display: "inline-flex",
            height: 72,
            justifyContent: "center",
            mb: 2,
            position: "relative",
            width: 72,
            ...(isRestored && {
              animation: "networkSuccessPop 500ms ease-out",
              "&::after": {
                animation: "networkSuccessRing 800ms ease-out",
                border: "2px solid",
                borderColor: "success.main",
                borderRadius: "50%",
                content: '""',
                inset: 0,
                position: "absolute",
              },
              "@keyframes networkSuccessPop": {
                "0%": { opacity: 0, transform: "scale(0.55)" },
                "65%": { opacity: 1, transform: "scale(1.12)" },
                "100%": { transform: "scale(1)" },
              },
              "@keyframes networkSuccessRing": {
                "0%": { opacity: 0.65, transform: "scale(0.85)" },
                "100%": { opacity: 0, transform: "scale(1.55)" },
              },
            }),
          }}
        >
          {isRestored ? (
            <CheckRoundedIcon sx={{ fontSize: 42 }} />
          ) : (
            <WifiOffRoundedIcon sx={{ fontSize: 38 }} />
          )}
        </Box>

        <Typography id="network-dialog-title" variant="h5" fontWeight={700}>
          {isRestored ? "Back online" : "No internet connection"}
        </Typography>
        <Typography
          id="network-dialog-description"
          color="text.secondary"
          sx={{ mt: 1 }}
        >
          {isRestored
            ? "Your connection has been restored. Resuming the application..."
            : "Please check your network connection. The application will resume automatically when you are back online."}
        </Typography>

        {!isRestored && (
          <Box
            sx={{
              alignItems: "center",
              color: "text.secondary",
              display: "flex",
              gap: 1,
              justifyContent: "center",
              mt: 3,
            }}
          >
            <CircularProgress color="inherit" size={18} />
            <Typography variant="body2">Waiting for connection...</Typography>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default NetworkStatusGuard;
